import { markAsClosed } from '@/store/api/notifications.api';
import { Notification } from '@/types/notification.types';
import { createContext, useCallback, useMemo, useState } from 'react';

interface NotificationContextType {
  notifications: Notification[];
  addNotifications: (items: Notification[]) => void;
  removeNotification: (id: number) => void;
  clearNotifications: () => void;
}

export const NotificationContext = createContext<NotificationContextType | null>(null);

export const NotificationProvider = ({ children }) => {
  const [notifications, setNotifications] = useState<Notification[]>([]);

  const addNotifications = useCallback((items: Notification[]) => {
    setNotifications(prev => {
      const map = new Map(prev.map(n => [n.id, n]));

      items.forEach(item => {
        map.set(item.id, item);
      });

      return Array.from(map.values());
    });
  }, []);

  const removeNotification = useCallback(async (id: number) => {
    setNotifications(prev => prev.filter(n => n.id !== id));
    await markAsClosed(id);
  }, []);

  const clearNotifications = useCallback(() => {
    setNotifications([]);
  }, []);

  const value = useMemo(
    () => ({
      notifications,
      addNotifications,
      removeNotification,
      clearNotifications,
    }),
    [notifications, addNotifications, removeNotification, clearNotifications],
  );

  return <NotificationContext.Provider value={value}>{children}</NotificationContext.Provider>;
};
